<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';
import Modal from '../../Modal.vue';
import { useLocale } from '../../../composables/useLocale';
import SelectGroupMemebers from '../../../containers/SelectGroupMemebers.vue';
import useModal, { modals, ModalTypes } from '../../../composables/useModal';
import { useManageGroup } from '../../../composables/useManageGroup';
import { useSidebarGroup } from '../../../composables/useSidebarGroup';
import useToast from '../../../composables/useToast';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import ConfirmUnsavedChangesModal from '../../../el/ConfirmUnsavedChangesModal.vue';
import { useAppEvents } from '../../../events/appEvents';

const { t } = useLocale();
const { xs } = useDisplay();
const { toast } = useToast();

const props = defineProps<{
  name: ModalTypes;
  organizationUid: string;
  members: CustomerMgmtTypes.MembershipOutput[];
  group: CustomerMgmtTypes.GetGroupResponse;
}>();

const { addMembersToGroup, removeMembersFromGroup, addingMembersProgress, removingMembersProgress } = useManageGroup();
const { getMembers } = useSidebarGroup();
const { emitAppEvent } = useAppEvents();

const { close } = useModal(props.name);
const { open: openConfirmUnsavedChangesModal } = useModal(modals.CONFIRM_UNSAVED_CHANGES);

const initialMemebersLoading = ref(false);
const updatedingMembers = ref(false);

const groupMembers = ref<string[]>(props.members.map((m) => m.user.uid));

watch(
  () => props.members,
  () => {
    groupMembers.value = props.members.map((membership) => membership.user.uid);
  },
);

const currentMembershipsGroupUids = computed(() => props.members.map((membership) => membership.user.uid));

const changes = computed(() => {
  const add = groupMembers.value.filter((userId) => !currentMembershipsGroupUids.value.includes(userId));
  const remove = currentMembershipsGroupUids.value.filter((userUid) => !groupMembers.value.includes(userUid));
  return { add, remove };
});

const updateMemebers = async () => {
  updatedingMembers.value = true;
  const { add, remove } = changes.value;
  const [addResponses, removeResponses] = await Promise.all([
    addMembersToGroup(props.group.uid, add),
    removeMembersFromGroup(props.group.uid, remove),
  ]);

  const failedAddResponses = addResponses.filter((r) => r.status !== 'successful');
  const failedRemoveResponses = removeResponses.filter((resonse) => resonse.status !== 'successful');

  const numberOfErrors = failedAddResponses.length + failedRemoveResponses.length;

  if (numberOfErrors === 0) {
    toast(t('lcp.user_groups_successfully_saved'), 'success');
  } else {
    if (numberOfErrors === addResponses.length + removeResponses.length) {
      toast(t('lcp.user_groups_save_failed'), 'error');
    } else {
      toast(
        t('lcp.user_groups_save_some_failed', {
          addedCount: failedAddResponses.length,
          removedCount: failedAddResponses.length,
        }),
        'warning',
      );
    }
  }

  await getMembers(props.group.uid, props.organizationUid);
  emitAppEvent({ type: 'membership:updated', organizationUid: props.organizationUid });
  updatedingMembers.value = false;
  reset();
  close();
};

const actionButtonLabel = computed(() => {
  if (groupMembers.value.length > 0) {
    return `${t('lcp.save_count_members', { count: groupMembers.value.length })}`;
  }
  return t('lcp.save');
});

const hasChange = computed(() => {
  const { add, remove } = changes.value;
  return add.length + remove.length > 0;
});

let waitingAfterUpdateProgress = ref(1);

const onClose = () => {
  if (updatedingMembers.value) {
    toast(t('lcp.group_members_updating'), 'warning');
    return;
  }

  if (hasChange.value) {
    openConfirmUnsavedChangesModal();
    return;
  }
  reset();
  close();
};

const reset = () => {
  groupMembers.value = props.members.map((m) => m.user.uid);
  addingMembersProgress.value = 0;
  removingMembersProgress.value = 0;
  waitingAfterUpdateProgress.value = 1;
  initialMemebersLoading.value = false;
  updatedingMembers.value = false;
};

const discardChangesAndClose = () => {
  reset();
  close();
};

const saveProgress = computed(() => {
  const { add, remove } = changes.value;
  return ((removingMembersProgress.value + addingMembersProgress.value) / (add.length + remove.length)) * 100;
});
</script>

<template>
  <Modal
    :name="name"
    :title="
      xs
        ? t('lcp.manage_group_members_for_short', { groupName: group.name })
        : t('lcp.manage_group_members_for', { groupName: group.name })
    "
    :action="updateMemebers"
    :action-disabled="updatedingMembers || !hasChange"
    :action-label="actionButtonLabel"
    :show-close-btn="!xs"
    :show-cancel-action="!xs"
    :cancel-action="onClose"
    :top-left-btn-action="xs ? onClose : undefined"
    :fullscreen="xs"
    background-color="surface-container"
    width="970"
    :loading="updatedingMembers"
    disableAutoClose
    @close="onClose"
  >
    <ConfirmUnsavedChangesModal :name="modals.CONFIRM_UNSAVED_CHANGES" @confirm="discardChangesAndClose" />
    <SelectGroupMemebers
      v-model="groupMembers"
      :initialSelectedMembers="currentMembershipsGroupUids"
      :organization-uid="organizationUid"
      :group-uid="group.uid"
    />
    <v-progress-linear
      v-if="updatedingMembers"
      v-model="saveProgress"
      color="primary"
      height="4"
      rounded
      class="mt-4"
    ></v-progress-linear>
  </Modal>
</template>
